import React from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { base44 } from "@/api/base44Client";
import AdMarkdownBody from "@/components/ads/AdMarkdownBody";
import ArticleLeaderboardAd from "@/components/ads/ArticleLeaderboardAd";
import ArticleTrustSignals from "@/components/public/ArticleTrustSignals";
import LearningStructuredData from "@/components/public/LearningStructuredData";
import { ensureWireArticleLength, countWords } from "@/lib/wireLongformContent";
import { getWireCopy, getWireFallbackArticle } from "@/i18n";

const SITE_URL = "https://marstick.com";

const upsertHeadTag = (selector, tagName, attributes) => {
  let tag = document.querySelector(selector);
  if (!tag) {
    tag = document.createElement(tagName);
    document.head.appendChild(tag);
  }
  Object.entries(attributes).forEach(([key, value]) => value != null && tag.setAttribute(key, value));
};

const upsertCanonical = (href) => {
  let tag = document.querySelector('link[rel="canonical"]');
  if (!tag) {
    tag = document.createElement("link");
    tag.setAttribute("rel", "canonical");
    document.head.appendChild(tag);
  }
  tag.setAttribute("href", href);
};

export default function WireArticlePage() {
  const { lang = "en", slug } = useParams();
  const copy = getWireCopy(lang);

  const { data: records = [] } = useQuery({
    queryKey: ["wire-article", slug, lang],
    queryFn: () => base44.entities.WireArticle.filter({ slug, status: "published", language: lang }, "-published_date", 1),
    enabled: !!slug,
  });

  const fallback = getWireFallbackArticle(slug, lang);
  const record = records[0];
  const article = record || fallback;
  const displayCategory = copy.categoryLabels?.[article.category] || article.category;
  const excerpt = article.excerpt || article.meta_description;
  const body = ensureWireArticleLength({ markdown: record?.body_content || copy.fallbackBody, article, targetWords: 1500 });
  const readMinutes = Math.max(record?.estimated_reading_time || 0, Math.ceil(countWords(body) / 220));
  const readTime = `${readMinutes} ${copy.minRead}`;
  const articleUrl = `/${lang}/wire/${slug}`;
  const canonicalUrl = `${SITE_URL}${articleUrl}`;

  React.useEffect(() => {
    if (!article?.title) return;
    const title = `${article.title} | Marstick Wire`;
    const description = excerpt || copy.description || copy.intro;
    const image = article.hero_image;
    const published = article.published_date || article.created_date;
    const modified = article.updated_date_custom || article.updated_date || published;

    document.title = title;
    upsertHeadTag('meta[name="description"]', "meta", { name: "description", content: description });
    upsertHeadTag('meta[property="og:type"]', "meta", { property: "og:type", content: "article" });
    upsertHeadTag('meta[property="og:title"]', "meta", { property: "og:title", content: title });
    upsertHeadTag('meta[property="og:description"]', "meta", { property: "og:description", content: description });
    upsertHeadTag('meta[property="og:url"]', "meta", { property: "og:url", content: canonicalUrl });
    upsertHeadTag('meta[property="article:section"]', "meta", { property: "article:section", content: displayCategory });
    upsertHeadTag('meta[property="article:author"]', "meta", { property: "article:author", content: article.author || copy.author });
    if (published) upsertHeadTag('meta[property="article:published_time"]', "meta", { property: "article:published_time", content: published });
    if (modified) upsertHeadTag('meta[property="article:modified_time"]', "meta", { property: "article:modified_time", content: modified });
    if (image) upsertHeadTag('meta[property="og:image"]', "meta", { property: "og:image", content: image });
    upsertHeadTag('meta[name="twitter:card"]', "meta", { name: "twitter:card", content: image ? "summary_large_image" : "summary" });
    upsertHeadTag('meta[name="twitter:title"]', "meta", { name: "twitter:title", content: title });
    upsertHeadTag('meta[name="twitter:description"]', "meta", { name: "twitter:description", content: description });
    if (image) upsertHeadTag('meta[name="twitter:image"]', "meta", { name: "twitter:image", content: image });
    upsertCanonical(canonicalUrl);
  }, [article, canonicalUrl, copy.author, copy.description, displayCategory, excerpt]);

  return (
    <article className="maritime-container py-8 md:py-14">
      <LearningStructuredData
        type="NewsArticle"
        article={{
          ...article,
          meta_description: excerpt,
          image_urls: article.hero_image ? [article.hero_image] : [],
          created_date: article.published_date || article.created_date,
          updated_date: article.updated_date_custom || article.updated_date || article.published_date || article.created_date,
        }}
        url={articleUrl}
        breadcrumbs={[{ name: copy.home, href: `/${lang}` }, { name: copy.wire, href: `/${lang}/wire` }, { name: article.title, href: articleUrl }]}
      />
      <nav className="mb-6 text-sm font-bold text-muted-foreground"><Link to={`/${lang}/wire`}>{copy.wire}</Link> / <span className="text-foreground">{displayCategory}</span></nav>
      <header data-article-intro className="mx-auto max-w-4xl text-center">
        <p className="text-sm font-black uppercase tracking-[0.22em] text-primary">{displayCategory} · {readTime}</p>
        <h1 className="mt-4 font-heading text-4xl font-black leading-tight md:text-6xl">{article.title}</h1>
        <p className="mt-5 text-xl leading-8 text-muted-foreground">{excerpt}</p>
        <p className="mt-5 text-sm font-bold text-muted-foreground">{copy.by} {article.author || copy.author}</p>
      </header>
      <ArticleLeaderboardAd className="my-8" />
      {article.hero_image && <img src={article.hero_image} alt="" className="mx-auto mt-10 max-h-[32rem] w-full max-w-5xl rounded-[2rem] object-cover shadow-xl" loading="eager" fetchPriority="high" />}
      <ArticleTrustSignals
        author={article.author || copy.author}
        updatedDate={article.updated_date_custom || article.published_date || article.created_date}
        category={displayCategory}
        readingTime={readTime}
        lang={lang}
      />
      <AdMarkdownBody
        markdown={body}
        className="prose prose-lg mx-auto mt-10 max-w-3xl prose-headings:font-black prose-a:text-primary dark:prose-invert"
      />
    </article>
  );
}